import SessionHelper from '@stubhub/session-helper';

/**
 * This helper function was extracted from getNavConfig() function to reuse the logic and follow SOLID principals (:D)
 * @param {number}  storeId                         An integer value representing different stores.
 * @param {string}  locale
 * @param {Boolean} userSignedIn                    Indicator of whether the user is logged in or not.
 * @param {Boolean} [isSessionAuthEnabled=false]    Indicator of whether feature toggle for controlling Central Login is turned on or not.
 */
export const getLinkConfig = (storeId, locale, userSignedIn = false, isSessionAuthEnabled = false) => {
  let linkConfig;
  let linkConfigByLocale;
  let signedOutUserLinkConfig;
  const defaultLinkConfig = require('../config/nav-links.config.json');
  if (!userSignedIn && isSessionAuthEnabled) {
    signedOutUserLinkConfig = _getLinkConfigForSignedOutUser(defaultLinkConfig);
  }
  try {
    linkConfig = require(`../config/nav-links.config.${storeId}.json`);
    /* istanbul ignore else */
    if (locale) {
      try {
        linkConfigByLocale = require(`../config/nav-links.config.${storeId}.${locale}.json`);
      } catch (e) {
        // ignore
      }
    }
  } catch (e) {
    // ignore
  }

  return {...defaultLinkConfig, ...signedOutUserLinkConfig, ...linkConfig, ...linkConfigByLocale};
};

/**
 * This method is to get the initiated data for Nav menu including the links.
 * @param {number}  storeId                                An integer value representing different stores.
 * @param {string}  locale
 * @param {Boolean} [enableBulletLogin=false]              Indicator of whether bullet-login is turned on or not.
 * @param {Boolean} [hasDiscoveryVipCategory=false]
 * @param {Boolean} [hasDiscoveryCitiesCategory=false]
 * @param {Boolean} [enableExpandedMenu=false]
 * @param {Boolean} [isSessionAuthEnabled=false]           Indicator of whether feature toggle for controlling Central Login is turned on or not.
 */
const getNavConfig = (
  storeId,
  locale,
  enableBulletLogin,
  hasDiscoveryVipCategory,
  hasDiscoveryCitiesCategory,
  enableExpandedMenu,
  isSessionAuthEnabled
) => {
  storeId = storeId || 1;
  locale = locale || '';
  enableBulletLogin = enableBulletLogin || false;
  hasDiscoveryVipCategory = hasDiscoveryVipCategory || false;
  hasDiscoveryCitiesCategory = hasDiscoveryCitiesCategory || false;
  enableExpandedMenu = enableExpandedMenu || false;
  isSessionAuthEnabled = isSessionAuthEnabled || false;
  
  const userSignedIn = SessionHelper.loginStatus();
  const excludeConfig = userSignedIn
    ? ['sign-in', 'sign-in-bullet']
    : ['profile', enableBulletLogin ? 'sign-in' : 'sign-in-bullet'];
  const defaultNavConfig = require('../config/nav.config.json');
  let navConfig;

  try {
    navConfig = require(`../config/nav.config.${storeId}.json`);
  } catch (e) {
    navConfig = {};
  }
  navConfig = {...defaultNavConfig, ...navConfig};
  if (hasDiscoveryCitiesCategory && navConfig.discovery && Array.isArray(navConfig.discovery)) {
    navConfig.discovery = [...navConfig.discovery, {id: 'cities', type: 'link'}];
  }

  if (hasDiscoveryVipCategory && navConfig.discovery && Array.isArray(navConfig.discovery)) {
    navConfig.discovery = [...navConfig.discovery, {id: 'vip', type: 'link'}];
  }
  let menuConfig;
  const defaultMenuConfig = require('../config/nav-menu.config.json');
  try {
    menuConfig = require(`../config/nav-menu.config.${storeId}.json`);
  } catch (e) {
    menuConfig = {};
  }
  let expandedMenuConfig = {};
  if (enableExpandedMenu) {
    try {
      expandedMenuConfig = require(`../config/nav-menu.config.${storeId}.expanded.json`);
    } catch (e) {
      expandedMenuConfig = {};
    }
  }
  menuConfig = {...defaultMenuConfig, ...menuConfig, ...expandedMenuConfig};
  menuConfig['menu-account'] = menuConfig['menu-account'].filter((config) => {
    return typeof config !== 'string' || excludeConfig.indexOf(config) < 0;
  });

  const linkConfig = getLinkConfig(storeId, locale, userSignedIn, isSessionAuthEnabled);

  /*
   * Get href / noFormat props for menu item config
   * cannot reach instance without item given
   */
  /* istanbul ignore next */
  const deconstructMenuItemHref = (item = {}) => {
    return typeof item === 'string' ? {href: item} : item;
  };

  const getMenuItems = (id) => {
    // Do not have menuConfigs with type link, please remove ignore if added in future
    /* istanbul ignore next */
    return menuConfig[id]
      ? menuConfig[id].map((item) => {
          if (typeof item === 'string') {
            return {
              id: item,
              type: 'link',
              ...deconstructMenuItemHref(linkConfig[item]),
            };
          } else if (item.type === 'link') {
            return {
              id: item.id,
              type: 'link',
              ...deconstructMenuItemHref(linkConfig[item.id]),
            };
          } else if (item.type === 'menu') {
            return {
              id: item.id,
              type: 'menu',
              items: getMenuItems(item.id),
            };
          } else if (item.type === 'hover') {
            return {
              id: item.id,
              type: 'menu',
              items: getMenuItems(item.id),
              ...deconstructMenuItemHref(linkConfig[item.id]),
            };
          } else if (item.type === 'section') {
            return {
              id: item.id,
              type: 'section',
              items: getMenuItems(item.id),
            };
          }

          return undefined;
        })
      : [];
  };

  const data = {};

  /*
   * Filter the navigation config to remove the sign-in menu when authenticated user cookie set
   * TODO: Remove this once login is implemented in Bullet
   *
   */
  const navConfigPath = navConfig.actions;
  navConfig.actions = navConfigPath.filter((config) => excludeConfig.indexOf(config.id) < 0);
  Object.keys(navConfig).forEach((groupKey) => {
    data[groupKey] = navConfig[groupKey].map((item) => {
      /* istanbul ignore else */
      if (item.type === 'link') {
        return {
          id: item.id,
          type: 'link',
          ...deconstructMenuItemHref(linkConfig[item.id]),
          target: item.target,
        };
      } else if (item.type === 'menu') {
        return {
          id: item.id,
          type: 'menu',
          items: getMenuItems(item.id),
        };
      } else if (item.type === 'hover') {
        return {
          id: item.id,
          type: 'menu',
          items: getMenuItems(item.id),
          ...deconstructMenuItemHref(linkConfig[item.id]),
        };
      } else if (item.type === 'subMenu') {
        return item;
      }

      return undefined;
    });
  });

  return data;
};

const _getLinkConfigForSignedOutUser = (defaultLinkConfig) => {
  const {origin, originHref} = _getOriginAndHrefValues();
  if (origin && originHref) {
    return {
      ...defaultLinkConfig,
      'sign-in': `${origin}/login/signup/?redirect=${encodeURIComponent(originHref)}`,
      'my-tickets': `${origin}/login/signup/?redirect=${encodeURIComponent(origin + defaultLinkConfig['my-tickets'])}`,
      listings: `${origin}/login/signup/?redirect=${encodeURIComponent(origin + defaultLinkConfig.listings)}`,
      sales: `${origin}/login/signup/?redirect=${encodeURIComponent(origin + defaultLinkConfig.sales)}`,
    };
  }

  return {
    ...defaultLinkConfig,
  };
};

const _getOriginAndHrefValues = () => {
  if (__CLIENT__ && window) {
    const origin =
      window.location.origin ||
      `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }`;
    const originHref = window.location.href;

    return {origin, originHref};
  }

  return {origin: null, originHref: null};
};

export default getNavConfig;
